export const basemapsArticle = {
    id: 'basemaps',
    title: 'Подложка',
    category: 'Режим рисования',
    requiredModule: null,
    sandboxType: null,
    summary: 'Загрузка растровых генпланов и мастер-планов, наложение на карту, масштабирование, поворот и оцифровка.',
    markdown: `
> **Инструмент «Подложка»** позволяет загрузить графический мастер-план, архитектурный проект или схему межевания в виде обычной картинки, точно наложить её поверх спутниковой карты и оцифровать (перерисовать) объекты прямо с неё, как по кальке.

---

## 1. Зачем нужен инструмент «Подложка»

Когда у вас есть проект посёлка от архитекторов или план межевания в формате изображения (JPG, PNG), вам не нужно вручную высчитывать координаты каждого угла:

:::grid cols=2
:::card icon="layers" title="1. Наложение мастер-плана"
Загрузите цветной архитектурный генплан посёлка или концепт застройки и наложите его точно на реальную спутниковую карту.
:::

:::card icon="file-text" title="2. Схемы межевания"
Используйте чертежи кадастровых инженеров с номерами участков и границами для точной привязки к местности.
:::

:::card icon="view" title="3. Оцифровка как по кальке"
Сделайте подложку полупрозрачной и обводите участки, дома и дороги стандартными инструментами рисования поверх чертежа.
:::

:::card icon="settings" title="4. Точная подгонка"
Интуитивные маркеры позволяют перемещать, вращать, растягивать и масштабировать чертёж под реальные ориентиры.
:::
:::

> 🔒 **Подложка видна только вам (в панели администратора):**  
> Загруженное изображение является **исключительно вашим внутренним рабочим черновиком**. Посетители и клиенты на публичной карте посёлка его **никогда не увидят**. Вы можете спокойно экспериментировать, двигать, масштабировать чертёж или оставлять его на карте столько, сколько нужно — это абсолютно безопасно и не повлияет на публичный вид генплана.

---

## 2. Пошаговая работа с подложкой: от загрузки до оцифровки

:::card icon="click" title="4 шага привязки чертежа:"
1. **Загрузите файл:** на верхней панели выберите инструмент **«Подложка»** (иконка слоёв) и укажите файл изображения (JPG или PNG) на вашем компьютере.
2. **Переместите чертёж:** зажмите **красный маркер в центре** и перетащите изображение на нужную территорию карты.
3. **Поверните и масштабируйте:**
   * Потяните за **синие маркеры по углам**, чтобы подогнать размер подложки под границы местности.
   * С помощью слайдера **«Вращение»** (или кнопок \`-15°\` / \`+15°\`) поверните чертёж так, чтобы его дороги совпали с реальными дорогами на спутнике.
4. **Настройте прозрачность и сохраните:**
   * Отрегулируйте ползунок **«Прозрачность»** (например, до 50–70%), чтобы сквозь картинку были видны ориентиры (лес, существующие заборы, перекрёстки).
   * Нажмите зелёную кнопку **«Сохранить»**.
:::

---

## 3. Элементы панели управления подложкой

При активном инструменте на экране появляется удобная плавающая панель управления:

| Элемент управления | Что делает | Для чего используется |
| :--- | :--- | :--- |
| 🔴 **Центральный маркер** | Свободное перемещение | Сдвигает весь чертёж целиком в нужную точку карты. |
| 🔵 **Угловые маркеры** | Трансформация углов | Растягивают или сжимают стороны изображения для устранения искажений. |
| **Слайдер «Прозрачность»** | Регулировка видимости (0–100%) | Позволяет видеть спутниковую подложку сквозь загруженный чертёж. |
| **Слайдер «Вращение» и ±15°** | Точный поворот | Разворачивает чертёж под углом от -180° до +180°. |
| **Кнопки «+» и «−»** | Масштабирование | Плавно увеличивают или уменьшают размер подложки. |
| 🟢 **Кнопка «Сохранить»** | Фиксация на карте | Надёжно сохраняет привязку в памяти браузера для дальнейшей работы. |
| 🔴 **Кнопка «Удалить»** | Очистка карты | Удаляет привязанную подложку, когда все объекты уже оцифрованы. |

---

## 4. Как перерисовывать объекты с подложки

После нажатия кнопки **«Сохранить»** подложка остаётся зафиксированной на карте на заданном месте:

1. Перейдите в режим черчения и выберите нужный инструмент: **«Участки»**, **«Здания»** или **«Линии»**.
2. Кликайте по углам участков и контурам строений прямо поверх наложенного чертежа.
3. После того как все объекты посёлка перенесены в векторный вид, снова активируйте инструмент **«Подложка»** и нажмите **«Удалить»** — на карте останется чистый, интерактивный цифровой генплан!
`
};
